import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useAuth } from "../../../auth/auth";
import styles from "./GenerateWeeklyHours.module.css";
import { generateWorkHoursReport, getAllClients, getProjectsByCompany } from "../../../api/apiCall";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useUKRailWeek } from "../../../hooks/useUKRailWeek";

const GenerateWeeklyHours = () => {
  const { state } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [clients, setClients] = useState<any>([]);
  const [projects, setProjects] = useState<any>([]);
  const [isDateRange, setIsDateRange] = useState(false);
  const {currentWeek, currentYear} = useUKRailWeek();

  const { register, handleSubmit, control, watch, setValue, getValues } = useForm<any>({
    defaultValues: {
      week: currentWeek,
      year: currentYear,
      startDate: "",
      endDate: "",
      clientId: "",
      projectId: "",
    },
  });

  const watchFields = watch();

  const getClients = async () => {
    const response = await getAllClients(state.user?.token);
    if (response) {
      setClients(response);
    }
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    
    let formattedData: any = {
      clientId: data?.clientId?.id,
      projectId: data?.projectId?.id,
    };

    if (isDateRange) {
      formattedData = {
        ...formattedData,
        startDate: data?.startDate,
        endDate: data?.endDate
      };
    } else {
      formattedData = {
        ...formattedData,
        week: data?.week,
        year: data?.year
      };
    }

    const response = await generateWorkHoursReport(state.user?.token, formattedData);
    if (response?.status === 200) {
      toast.success("Report generated successfully!");
      setLoading(false);
    } else {
      setLoading(false);
      toast.error("Something went wrong!");
    }
  };

  const getProjects = async () => {
    if (!watchFields.clientId?.id) {
      setProjects([]);
      return;
    }
    
    setProjectsLoading(true);
    try {
      let params: any = {
        clientId: watchFields.clientId?.id,
      };

      if (isDateRange) {
        if (watchFields.startDate && watchFields.endDate) {
          params = {
            ...params,
            startDate: watchFields.startDate,
            endDate: watchFields.endDate
          };
        } else {
          setProjectsLoading(false);
          setProjects([]);
          return;
        }
      } else {
        if (watchFields.week && watchFields.year) {
          params = {
            ...params,
            week: watchFields.week,
            year: watchFields.year
          };
        } else {
          setProjectsLoading(false);
          setProjects([]);
          return;
        }
      }

      const response = await getProjectsByCompany(
        state.user?.token,
        params
      );
      
      const newProjects = response?.data || response || [];
      setProjects(newProjects);
      
      const currentProject = getValues("projectId");
      if (currentProject) {
        const projectStillExists = newProjects.some(
          (project: any) => project.id === currentProject.id
        );
        
        if (!projectStillExists) {
          setValue("projectId", null);
        }
      }
    } catch (error: any) {
      console.error("Error fetching projects:", error);
      toast.error(error?.message || "Failed to load projects");
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsDateRange(event.target.checked);
    if (event.target.checked) {
      setValue("week", currentWeek);
      setValue("year", currentYear);
      setValue("projectId", null);
    } else {
      setValue("startDate", "");
      setValue("endDate", "");
      setValue("projectId", null);
    }
  };

  useEffect(() => {
    getClients();
  }, []);

  useEffect(() => {
    if (watchFields.clientId?.id) {
      if (isDateRange) {
        if (watchFields.startDate && watchFields.endDate) {
          getProjects();
        } else {
          setProjects([]);
        }
      } else {
        if (watchFields.week && watchFields.year) {
          getProjects();
        } else {
          setProjects([]);
        }
      }
    } else {
      setProjects([]);
    }
  }, [
    watchFields.clientId, 
    watchFields.week, 
    watchFields.year,
    watchFields.startDate,
    watchFields.endDate,
    isDateRange
  ]);

  const today = new Date().toISOString().split('T')[0];

  return (
    <>
      <div className={styles.formContainer}>
        <div className={styles.timesheetForm}>
          <Box>
            <div className={styles.timesheetFormDiv}>
              <h2 className={styles.timesheetTitle}>Hourly Report</h2>
              <p className={styles.timesheetSubtitle}>
                Generate Hourly Report
              </p>
              
              <div style={{ margin: 15, display: 'flex', justifyContent: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isDateRange}
                      onChange={handleToggle}
                      color="primary"
                    />
                  }
                  label={isDateRange ? "Date Range" : "Week/Year"}
                />
              </div>

              {!isDateRange && (
                <>
                  <TextField
                    variant="outlined"
                    label="Week"
                    type="number"
                    placeholder="Enter Week"
                    className={styles.exportButton}
                    style={{ margin: 15 }}
                    {...register("week")}
                  />
                  <TextField
                    variant="outlined"
                    label="Year"
                    type="number"
                    placeholder="Enter Year"
                    className={styles.exportButton}
                    style={{ margin: 15 }}
                    {...register("year")}
                  />
                </>
              )}

              {isDateRange && (
                <div style={{ display: 'flex', gap: '15px', margin: 15, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <TextField
                    variant="outlined"
                    label="Start Date"
                    type="date"
                    className={styles.exportButton}
                    style={{ width: '190px' }}
                    {...register("startDate", { required: isDateRange })}
                    slotProps={{
                      htmlInput: {
                        max: watchFields.endDate || today
                      },
                      inputLabel: {
                        shrink: true,
                      }
                    }} />
                  <TextField
                    variant="outlined"
                    label="End Date"
                    type="date"
                    className={styles.exportButton}
                    style={{ width: '190px' }}
                    {...register("endDate", { required: isDateRange })}
                    slotProps={{
                      htmlInput: {
                        min: watchFields.startDate,
                        max: today
                      },

                      inputLabel: {
                        shrink: true,
                      }
                    }} />
                </div>
              )}

              <Controller
                name="clientId"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option: any) => option.companyName || ""}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option.id === value?.id
                    }
                    style={{ margin: 15 }}
                    value={field.value || null}
                    onChange={(_, value) => field.onChange(value)}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Client"
                        placeholder="Choose a Client"
                        size="medium"
                        sx={{ width: "400px" }}
                      />
                    )}
                    slotProps={{
                      listbox: {
                        style: {
                          maxHeight: 320,
                          overflow: "auto",
                        },
                      }
                    }}
                  />
                )}
              />
              
              {watchFields.clientId && 
                ((!isDateRange && watchFields.week && watchFields.year) || 
                (isDateRange && watchFields.startDate && watchFields.endDate)) && (
                <Controller
                  name="projectId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={projects}
                      loading={projectsLoading}
                      getOptionLabel={(option: any) => 
                        option.projectName 
                          ? `${option.projectName}${option.workerCount ? ` - (${option.workerCount} Workers)` : ''}` 
                          : ""
                      }
                      isOptionEqualToValue={(option: any, value: any) =>
                        option.id === value?.id
                      }
                      style={{ margin: 15 }}
                      value={field.value || null}
                      onChange={(_, value) => field.onChange(value)}
                      clearOnEscape
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Project"
                          placeholder={
                            projectsLoading 
                              ? "Loading projects..." 
                              : projects.length === 0 
                                ? "No projects available for this period" 
                                : "Choose a project (optional)"
                          }
                          size="medium"
                          sx={{ width: "400px" }}
                          slotProps={{
                            ...params.slotProps,

                            input: {
                              ...params.slotProps.input,
                              endAdornment: (
                                <>
                                  {projectsLoading && (
                                    <CircularProgress color="inherit" size={20} />
                                  )}
                                  {params.slotProps.input.endAdornment}
                                </>
                              ),
                            }
                          }}
                        />
                      )}
                      slotProps={{
                        listbox: {
                          style: {
                            maxHeight: 320,
                            overflow: "auto",
                          },
                        }
                      }}
                    />
                  )}
                />
              )}

              {/* Show message if client is selected but time parameters are missing */}
              {watchFields.clientId && !projectsLoading && (
                <>
                  {!isDateRange && (!watchFields.week || !watchFields.year) && (
                    <div style={{ color: '#666', margin: '10px 0', textAlign: 'center' }}>
                      Please enter week and year to load projects
                    </div>
                  )}
                  {isDateRange && (!watchFields.startDate || !watchFields.endDate) && (
                    <div style={{ color: '#666', margin: '10px 0', textAlign: 'center' }}>
                      Please select start and end dates to load projects
                    </div>
                  )}
                </>
              )}

              <Button
                variant="contained"
                disabled={
                  loading || 
                  projectsLoading || 
                  (isDateRange ? (!watchFields.startDate || !watchFields.endDate) : (!watchFields.week || !watchFields.year))
                }
                size="medium"
                style={{
                  boxShadow: "none",
                  marginTop: 25,
                  backgroundColor: "#1976d2",
                  color: "white",
                }}
                className={styles.exportButton}
                onClick={handleSubmit(onSubmit)}
              >
                {loading ? 
                  <CircularProgress size={25} style={{ color: "white" }} />
                :
                  "Export Weekly Hours"
                }
              </Button>
              <div
                style={{ textAlign: "center", margin: 4, marginBottom: -10 }}
              >
                {loading && "This might take a while"}
                {projectsLoading && "Loading projects..."}
              </div>
            </div>
          </Box>
        </div>
      </div>
    </>
  );
};

export default GenerateWeeklyHours;
